// © Microsoft Corporation. All rights reserved.
import React, { useState, useEffect, useCallback } from 'react';
import GroupCall from './containers/GroupCall';
import { createStore, applyMiddleware } from 'redux';
import { Provider } from 'react-redux';
import { reducer } from './core/reducers';
import thunk from 'redux-thunk';
import EndCall from './components/EndCall';
import HomeScreen from './components/HomeScreen';
import ConfigurationScreen from './containers/Configuration';
import { v1 as createGUID } from 'uuid';
import { loadTheme, initializeIcons } from '@fluentui/react';
import { utils, IsCandidateMode, IsSecondaryStream, IsCentralScreenMode } from './Utils/Utils';
import { CallEndReason } from '@azure/communication-calling';
import { createThread, SetSelectedCamera, SetSelectedMicrophone, SignalRUserDisconnect } from './core/sideEffects';
import { URLOptionsConstants, TextMessages, SetTextLanguage, FormLanguage } from './core/constants';
import { HubConnection } from '@microsoft/signalr';

const sdkVersion = require('../package.json').dependencies['@azure/communication-calling'];
const lastUpdated = `Last Updated ${utils.getBuildTime()} with @azure/communication-calling:${sdkVersion}`;

loadTheme({});
initializeIcons();

const store = createStore(reducer, applyMiddleware(thunk));
export const signalR = require("@microsoft/signalr");
let signalrUrl = window.location.protocol + "//" + window.location.host + "/signalr";
console.log('SignalR: signalrUrl + ' + signalrUrl);

export const connection: HubConnection = new signalR.HubConnectionBuilder()
    .withUrl(signalrUrl)
    .withAutomaticReconnect()
    .build();
connection.serverTimeoutInMilliseconds = 7200000; //2 hours
connection.keepAliveIntervalInMilliseconds = 480000; //4 minutes

async function StartSignalRConnection() {
    try {
        if ((connection.state !== signalR.HubConnectionState.Connected) &&
            (connection.state !== signalR.HubConnectionState.Connecting)) {
            await connection.start();

            console.assert(connection.state === signalR.HubConnectionState.Connected);
            console.log("SignalR: Connected connectionid: " + connection.connectionId + ", mode iscandidate: " + IsCandidateMode() + ", mode IsSecondaryStream: " + IsSecondaryStream() + ", status: " + connection.state);
        }
    } catch (err) {
        console.error('SignalR: ' + err);
        setTimeout(() => StartSignalRConnection(), 10000);
    }
};

export const StartSignalR = async () => {
    await StartSignalRConnection();
};

const DisconnectUser = () => {
    const urlParams = new URLSearchParams(window.location.search.substring(1));
    const threadId = urlParams.get(URLOptionsConstants.THREADID);
    const userId = urlParams.get(URLOptionsConstants.USERID);
    if (threadId && userId) {
        SignalRUserDisconnect(threadId, userId);
        console.info('SignalR:userID ' + userId + ' Is Disconnected');
    }
};

const AlertIframeParent = async () => {
    try {
        if (document.referrer) {
            window.parent.postMessage({ type: "listening", origin: window.location.origin }, new URL(document.referrer).origin);
        }
    } catch (err) {
        console.error('AlertIframeParent: ' + err);
    }
}

const OnError = (event: any) => {
    console.error(event.message);
}

const SendErrorToIframeParent = async (msg: any) => {
    try {
        if (document.referrer) {
            window.parent.postMessage({ type: "error", origin: window.location.origin, message: msg },
                new URL(document.referrer).origin);
        }
    } catch (err) {
        console.warn('AlertIframeParent: ' + err);
    }
}

let init: boolean = true;

if (init) {
    SetSelectedMicrophone(-1);
    SetSelectedCamera(-1);
    init = false;
}

const App = (): JSX.Element => {
    const [page, setPage] = useState('home');

    const [callEndReason, setCallEndReason] = useState<CallEndReason | undefined>();
    const [groupId, setGroupId] = useState('');
    const [chatId, setChatId] = useState('');
    const [screenWidth, setScreenWidth] = useState(0);
    const [localVideoStream, setLocalVideoStream] = useState(undefined);

    const unsupportedStateHandler = useCallback((): void => setPage('unsupported'), []);

    var newchatid: string;

    useEffect(() => {
        window.addEventListener("beforeunload", DisconnectUser);
        window.addEventListener("error", OnError);
        (function () {
            var oldLog = console.error;
            console.error = function (errMsg:any) {
                SendErrorToIframeParent(errMsg);
                oldLog.apply(console, [errMsg]);
            };
        })();

        return () => {
            window.removeEventListener("beforeunload", DisconnectUser);
            window.addEventListener("error", OnError);
        };
    }, []);

    useEffect(() => {
        const setWindowWidth = (): void => {
            const width = typeof window !== 'undefined' ? window.innerWidth : 0;
            setScreenWidth(width);
        };
        setWindowWidth();
        window.addEventListener('resize', setWindowWidth);
        return (): void => window.removeEventListener('resize', setWindowWidth);
    }, []);

    const getGroupIdFromUrl = (): string | null => {
        const urlParams = new URLSearchParams(window.location.search.substring(1));

        return urlParams.get('groupId');
    };

    const getChatIdFromUrl = (): string | null => {
        const urlParams = new URLSearchParams(window.location.search.substring(1));
        return urlParams.get(URLOptionsConstants.THREADID);
    };

    const setLangFromUrl = (): void => {
        const urlParams = new URLSearchParams(window.location.search.substring(1));
        if (urlParams != null) {
            SetTextLanguage(Number(urlParams.get(URLOptionsConstants.LANGUAGE)));
        }
        else
        {
            SetTextLanguage(FormLanguage.English);
        }
    };

    const rejoinCentralScreen = (): void => {
        if (callEndReason?.code === 410 && callEndReason?.subCode === 301005 && IsCentralScreenMode() ) {

            setTimeout(function () {
                window.location.reload();
            }, 1000);
        }
    };
    
    const getGroupId = (): string => {
        if (groupId) return groupId;
        // const uriGid = getGroupIdFromUrl()?.split('?')[0];
        const uriGid = getGroupIdFromUrl();
        const gid = uriGid == null || uriGid === '' ? createGUID() : uriGid;
        setGroupId(gid);
        return gid;
    };
    var newchatid: string;
    var id: string;

    setTimeout(function () {
        StartSignalR();
        AlertIframeParent();
    }, 1000);

    function delay() {
        // `delay` returns a promise
        return new Promise(function (resolve, reject) {
            // Only `delay` is able to resolve or reject the promise
            setTimeout(function () {
                resolve(createThread()); // After 3 seconds, resolve the promise with value 42
            }, 100);
        });
    }

    const getContent = (): JSX.Element => {
        setLangFromUrl();       
        if (page === 'home') {
            return (
                <HomeScreen
                    startCallHandler={(): void => {
                        delay()
                            .then(function (data) { // `delay` returns a promise
                                id = data as string;

                                if (chatId) return chatId;
                                const uriGid = getChatIdFromUrl();

                                const gid = uriGid == null || uriGid === '' ? id : uriGid;
                                setChatId(gid);
                                newchatid = gid
                                window.history.pushState({}, document.title, window.location.href + '&threadid=' + newchatid);
                                return gid;
                            })
                            .catch(function (v) {
                                // Or do something else if it is rejected
                                // (it would not happen in this example, since `reject` is not called).
                                return "error";
                            });

                        window.history.pushState({}, document.title, window.location.href + '?groupId=' + getGroupId());
                    }}
                />
            );
        } else if (page === 'configuration') {
            return (
                <ConfigurationScreen
                    startCallHandler={(): void => setPage('call')}
                    unsupportedStateHandler={unsupportedStateHandler}
                    callEndedHandler={(errorMsg: CallEndReason): void => {
                        setCallEndReason(errorMsg);
                        setPage('error');
                    }}
                    groupId={getGroupId()}
                    screenWidth={screenWidth}
                    localVideoStream={localVideoStream}
                    setLocalVideoStream={setLocalVideoStream}

                />
            );
        } else if (page === 'call') {
            return (
                <GroupCall
                    endCallHandler={(): void => setPage('endCall')}
                    groupId={getGroupId()}
                    screenWidth={screenWidth}
                    localVideoStream={localVideoStream}
                    setLocalVideoStream={setLocalVideoStream}
                />
            );
        } else if (page === 'endCall') {
            return (
                <EndCall
                    message={IsCandidateMode() ? TextMessages.CandidateEndCall :  TextMessages.EndCall}
                    rejoinHandler={(): void => {
                        window.location.reload();
                    }}
                />
            );
        } else if (page === 'unsupported') {
            window.document.title = 'Unsupported browser or usage';
            return (
                <span>
                    You are attempting to use this page with either an unsupported browser or invalid mode.<br /><br />
                    <a href="https://docs.microsoft.com/en-us/azure/communication-services/concepts/voice-video-calling/calling-sdk-features#javascript-calling-sdk-support-by-os-and-browser">
                        Learn more
                    </a>
                    &nbsp;about browsers and platforms supported by the web calling sdk
                </span>
            );
        } else if (page === 'error') {
            window.document.title = 'Call Ended';
            rejoinCentralScreen();
            return (
                <div>
                    <div>{`The call has ended with this error code (Code: ${callEndReason?.code} Subcode: ${callEndReason?.subCode})`}</div>

                    <div>
                        <a href="https://docs.microsoft.com/en-us/azure/communication-services/concepts/troubleshooting-info?tabs=csharp%2Cjavascript%2Cdotnet">
                            Learn more
                        </a>
                        &nbsp;about why this Azure Communication Services call has ended.
                    </div>
                </div>
            );
        } else {
            return <></>;
        }
    };

    if (getGroupIdFromUrl() && page === 'home') {
        setPage('configuration');
    }

    return <Provider store={store} >{getContent()}</Provider>;
};

window.setTimeout(() => {
    try {
        console.log(`Azure Communication Services: ${lastUpdated}`);
    } catch (e) { }
}, 0);

export default App;